import Dexie, { Table } from 'dexie';
import { AppSettings, SpinHistory, Wheel, WheelEntry } from '../types';
import { generateUniqueId } from '../utils/random';

export class SpinWheelDatabase extends Dexie {
  wheels!: Table<Wheel, string>;
  entries!: Table<WheelEntry, string>;
  history!: Table<SpinHistory, string>;
  settings!: Table<AppSettings, string>;

  constructor() {
    super('SpinWheelDB');

    this.version(1).stores({
      wheels: 'id, name, createdAt, updatedAt',
      entries: 'id, wheelId, position, createdAt',
      history: 'id, wheelId, timestamp',
      settings: 'id',
    });

    this.on('populate', () => this.seedInitialData());
  }

  async seedInitialData() {
    const now = Date.now();

    // Default global settings
    await this.settings.put({
      id: 'global_settings',
      soundEnabled: true,
      masterVolume: 0.75,
      defaultSpinDuration: 5,
      removeWinnerAfterSpin: false,
      confettiEnabled: true,
      reduceMotion: false,
      theme: 'dark',
      colorPalette: 'vibrant',
      autoOpenWinnerModal: true,
      wheelNameFontSize: 24,
    });

    // 1. Office Team Lucky Draw
    const teamWheelId = 'wheel_team_demo';
    await this.wheels.put({
      id: teamWheelId,
      name: '🎯 Office Team Lucky Draw',
      description: 'Pick a random team member for presentations or prizes',
      colorThemeId: 'vibrant',
      spinDuration: 5,
      removeWinnerAfterSpin: false,
      createdAt: now,
      updatedAt: now,
    });

    const teamNames = ['Sarah Miller', 'David Chen', 'Elena Rostova', 'Michael Scott', 'Priya Patel', 'Alex Morgan', 'James Wilson', 'Maya Lin'];
    const teamEntries: WheelEntry[] = teamNames.map((name, idx) => ({
      id: generateUniqueId(),
      wheelId: teamWheelId,
      label: name,
      position: idx,
      weight: 1,
      createdAt: now + idx,
    }));
    await this.entries.bulkPut(teamEntries);

    // 2. What's For Lunch?
    const lunchWheelId = 'wheel_lunch_demo';
    await this.wheels.put({
      id: lunchWheelId,
      name: "🍕 What's For Lunch?",
      description: 'End the lunch indecision once and for all!',
      colorThemeId: 'sunset',
      spinDuration: 5,
      removeWinnerAfterSpin: false,
      createdAt: now + 1,
      updatedAt: now + 1,
    });

    const lunchFoods = ['Neapolitan Pizza 🍕', 'Gourmet Burgers 🍔', 'Spicy Miso Ramen 🍜', 'Fresh Sushi Platter 🍣', 'Street Tacos 🌮', 'Thai Green Curry 🍲', 'Mediterranean Gyro 🥙', 'Healthy Grain Bowl 🥗'];
    const lunchEntries: WheelEntry[] = lunchFoods.map((food, idx) => ({
      id: generateUniqueId(),
      wheelId: lunchWheelId,
      label: food,
      position: idx,
      weight: 1,
      createdAt: now + 10 + idx,
    }));
    await this.entries.bulkPut(lunchEntries);

    // 3. Mega Giveaway
    const giveawayWheelId = 'wheel_giveaway_demo';
    await this.wheels.put({
      id: giveawayWheelId,
      name: '🎁 Mega Prize Giveaway',
      description: 'Exciting rewards for events and streams',
      colorThemeId: 'cyberpunk',
      spinDuration: 8,
      removeWinnerAfterSpin: true,
      createdAt: now + 2,
      updatedAt: now + 2,
    });

    const giveawayPrizes = ['PlayStation 5 🎮', '$100 Gift Card 💳', 'Noise-Cancelling Headphones 🎧', 'Smart Watch ⌚', 'Mystery Box 🎁', 'Pro Gaming Mouse 🖱️', 'Free Coffee for a Month ☕', 'VIP Event Ticket 🎟️'];
    const giveawayEntries: WheelEntry[] = giveawayPrizes.map((prize, idx) => ({
      id: generateUniqueId(),
      wheelId: giveawayWheelId,
      label: prize,
      position: idx,
      weight: 1,
      createdAt: now + 20 + idx,
    }));
    await this.entries.bulkPut(giveawayEntries);
  }
}

export const db = new SpinWheelDatabase();
